import BaseMapController from "./base_map_controller"
import { MapBounds } from "@/utils/map_interfaces"
import { ResourceMarker } from "app/frontend/interfaces/resource_marker"
import { Turbo } from "@hotwired/turbo-rails"
import { resourceCardTemplate } from '@/templates/resource_card'
import { i18n } from "../lib/i18n"

export default class extends BaseMapController {
  static targets = ["map", "cards", "searchInput", "filterButton"]
  static values = {
    ...BaseMapController.values,
    categoryColors: { type: Object }
  }

  declare readonly cardsTarget: HTMLElement
  declare readonly searchInputTarget: HTMLInputElement
  declare readonly filterButtonTarget: HTMLAnchorElement
  declare readonly categoryColorsValue: { [key: string]: string }
  declare readonly hasCardsTarget: boolean
  declare timeout: number | undefined

  protected async fetchMarkers(bounds?: MapBounds) {
    const url = new URL(this.markersUrlValue, window.location.origin)
    const currentParams = new URLSearchParams(window.location.search)

    // Preserve search and filter params
    currentParams.forEach((value, key) => {
      if (/filters|q/.test(key)) {
        url.searchParams.set(key, value)
      }
    })

    if (bounds) {
      Object.entries(bounds).forEach(([key, value]) => {
        url.searchParams.set(key, value.toString())
      })
    }

    const response = await fetch(url)
    const markers: ResourceMarker[] = await response.json()

    this.clearMarkers()
    this.renderMarkers(markers)
    this.renderCards(markers)
  }

  protected createMarker(markerData: ResourceMarker): google.maps.Marker {
    return new google.maps.Marker({
      position: { lat: markerData.latitude, lng: markerData.longitude },
      map: this.map!,
      title: markerData.info.name
    })
  }

  private renderCards(markers: ResourceMarker[]) {
    if (!this.hasCardsTarget) return

    if (markers.length === 0) {
      this.cardsTarget.innerHTML = this.renderNoResults()
      return
    }

    this.cardsTarget.innerHTML = `
      <div class="px-4 space-y-4">
        ${markers.map(marker => this.renderCard(marker)).join('')}
      </div>
    `
  }

  private renderCard(resource: ResourceMarker) {
    return `
      <div id="resource_${resource.id}_card"
           data-controller="template"
           data-template-value="resource-card-template"
           data-template-data-value='${JSON.stringify(resource)}'>
        ${resourceCardTemplate(resource)}
      </div>
    `
  }

  private renderNoResults() {
    return `
      <div class="text-center py-8">
        <p class="text-gray-500">${i18n.t('resources_map.no_resources_found')}</p>
        ${window.location.search ? `
          <a href="/resources" class="btn btn-ghost btn-sm mt-2" data-action="click->resources-map#clearFilters">${i18n.t('resources_map.clear_filters')}</a>
        ` : ''}
      </div>
    `
  }

  protected handleMarkerClick(markerData: ResourceMarker): () => void {
    return () => {
      this.openResourceModal(markerData)
    }
  }

  openResourceModal(eventOrMarker: Event | ResourceMarker) {
    if (eventOrMarker instanceof Event) {
      const target = eventOrMarker.currentTarget as HTMLElement
      const id = target.dataset.id
      if (id) {
        Turbo.visit(`/resources/${id}`, { action: 'advance' })
        this.map?.panTo({
          lat: parseFloat(target.dataset.lat || "0"),
          lng: parseFloat(target.dataset.lng || "0")
        })
      }
    } else {
      const marker = eventOrMarker
      Turbo.visit(`/resources/${marker.id}`, { action: 'advance' })
      this.map?.panTo({ lat: marker.latitude, lng: marker.longitude })
    }
  }

  handleSearch() {
    clearTimeout(this.timeout)

    this.timeout = window.setTimeout(() => {
      const url = new URL(window.location.href)
      const searchTerm = this.searchInputTarget.value.trim()

      if (searchTerm) {
        url.searchParams.set('q', searchTerm)
      } else {
        url.searchParams.delete('q')
      }

      window.history.replaceState({}, '', url.toString())
      this.updateFilterButtonHref()
      this.fetchMarkersForCurrentBounds()
    }, 100)
  }

  handleSearched(event: CustomEvent) {
    // The search-input controller has already updated the URL
    // We just need to fetch new markers
    this.fetchMarkersForCurrentBounds()
  }

  clearFilters(event: Event) {
    event.preventDefault()

    // Clear filter params from URL
    const url = new URL(window.location.pathname, window.location.origin)
    window.history.replaceState({}, '', url.toString())

    // Clear search input
    this.searchInputTarget.value = ''

    // Update filter button href
    this.updateFilterButtonHref()

    // Fetch new markers
    this.fetchMarkersForCurrentBounds()
  }

  async connect() {
    await super.connect()
    // Listen for filter events from the drawer
    document.addEventListener('resources:filtered', this.handleFilterEvent.bind(this))
  }

  disconnect() {
    super.disconnect()
    document.removeEventListener('resources:filtered', this.handleFilterEvent.bind(this))
  }

  handleFilterEvent(event: CustomEvent) {
    const { filters } = event.detail

    // Update URL with filter params
    const url = new URL(window.location.pathname, window.location.origin)
    const searchTerm = this.searchInputTarget.value.trim()

    // Preserve search term
    if (searchTerm) {
      url.searchParams.set('q', searchTerm)
    }

    // Add new filter params
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          url.searchParams.append(`filters[${key}]`, value.toString())
        }
      })
    }

    window.history.replaceState({}, '', url.toString())
    this.updateFilterButtonHref()
    this.fetchMarkersForCurrentBounds()
  }

  private updateFilterButtonHref() {
    const currentUrl = new URL(window.location.href)
    const filterUrl = new URL(this.filterButtonTarget.href)

    // Copy current search params to filter URL
    currentUrl.searchParams.forEach((value, key) => {
      if (key !== 'format') {
        filterUrl.searchParams.set(key, value)
      }
    })

    this.filterButtonTarget.href = filterUrl.toString()

    // Update active state
    if (currentUrl.searchParams.toString()) {
      this.filterButtonTarget.classList.add('btn-active')
    } else {
      this.filterButtonTarget.classList.remove('btn-active')
    }
  }
}